// Logic for the homeowner start page "Att göra nu" list: which action items are
// active, and in what order. Kept as a pure function so the conditions and the
// product-decided ordering are unit-testable independently of rendering.
//
// Unlike the company list (one card per item type), the person list is one row
// per concrete thing to act on — a specific contract, milestone or payment — so
// each row can deep-link straight to it.
//
// Order (product-decided): payments that block the project first, then items
// that need the person's review/approval/signature, then everything else.

import type { Contract, Milestone, ProjectFundingContract } from '../types';
import { canAttestMilestone } from './milestoneAttestation';
import { canSignContract } from './contractSigningAvailability';
import { contractTitle } from './contractTitle';
import { isRevisedDraft } from './revisedDraft';
import { sortByPriority, type TodoPriority } from './todoPriority';

export type PersonTodoKind =
  | 'payment'
  | 'approveMilestone'
  | 'objection'
  | 'conflict'
  | 'signContract'
  | 'reviewContract'
  | 'completeProfile';

export const PERSON_TODO_ORDER: readonly PersonTodoKind[] = [
  'payment',
  'approveMilestone',
  'objection',
  'conflict',
  'signContract',
  'reviewContract',
  'completeProfile',
];

/** How important each kind is (ClickUp 869fj6nkx): an objection or a missing
 * payment stops the project, the stage and the contract wait for the person,
 * the profile can wait. */
export const PERSON_TODO_PRIORITY: Record<PersonTodoKind, TodoPriority> = {
  payment: 'urgent',
  objection: 'urgent',
  conflict: 'urgent',
  approveMilestone: 'waiting',
  signContract: 'waiting',
  reviewContract: 'waiting',
  completeProfile: 'later',
};

export interface PersonTodo {
  /** Unique within the list (kind + entity id). */
  key: string;
  kind: PersonTodoKind;
  priority: TodoPriority;
  /** Since when the row has waited, for the order within a priority. */
  since?: string | null;
  projectId?: string;
  projectDescription?: string;
  /** The project's construction company — lets a person with several
   * providers (plumber, electrician, carpenter) tell whose card this is. */
  companyName?: string | null;
  contractId?: string;
  contractNumber?: number;
  contractDescription?: string;
  /** A changed contract the company sent after the person sent it back. */
  revised?: boolean;
  /** Sign: the ÄTA belongs to the open objection against this stage; signing
   * closes the objection (ClickUp 869fgj7uc). */
  objectionMilestoneNumber?: number;
  milestoneId?: string;
  milestoneNumber?: number;
  milestoneDescription?: string;
  /** Payment: amount still to be paid in, in öre. */
  amountOre?: number;
  /** Payment: a payment is already being processed for this contract. */
  paymentPending?: boolean;
}

export interface PersonTodoProject {
  id: string;
  description: string;
  companyName?: string | null;
  /** A completed project has nothing left to pay in. */
  status?: string | null;
}

export interface PersonTodoProfile {
  phone?: string | null;
  address?: string | null;
  postalCode?: string | null;
  city?: string | null;
}

export type PersonFundingAction = Pick<
  ProjectFundingContract,
  'contractId' | 'contractNumber' | 'remainingFundingNeedOre' | 'canPay' | 'status'
> & { projectId: string };

export interface PersonTodoInput {
  projects: PersonTodoProject[];
  /** Per-contract funding state from the payments API (all contracts; filtered here). */
  fundingActions: PersonFundingAction[];
  contracts: Contract[];
  milestones: Milestone[];
  /** null while the profile is still loading, so missing fields are never flagged early. */
  profile: PersonTodoProfile | null;
}

// Milestone delivery states where the person is part of an ongoing dispute
// (objection raised, remedy proposed, or escalated to Etappio).
const DISPUTE_STATUSES = new Set(['disputed', 'under_resolution', 'conflict']);

const isBlank = (value: string | null | undefined) => !value || !value.trim();

/** Required contact details for a homeowner (needed for agreements and for the
 * construction company to reach them). */
export const isPersonProfileComplete = (profile: PersonTodoProfile): boolean =>
  !isBlank(profile.phone) &&
  !isBlank(profile.address) &&
  !isBlank(profile.postalCode) &&
  !isBlank(profile.city);

/** Draft sent to the person and not yet answered — approve or reject. */
export const needsPersonDraftReview = (contract: Contract): boolean =>
  contract.draftStatus === 'draft_sent' &&
  !contract.personSigned &&
  !contract.supersededByContractId &&
  ['current', 'proposed'].includes(contract.versionStatus ?? 'current');

export const isMilestoneInDispute = (milestone: Milestone): boolean =>
  DISPUTE_STATUSES.has(milestone.deliveryStatus ?? '');

/** Conflict mode has been started on the stage; before that it is an objection. */
export const isMilestoneInConflict = (milestone: Milestone): boolean =>
  milestone.deliveryStatus === 'conflict' || Boolean(milestone.conflictStartedAt);

/** Milestone reported complete by the company and waiting for the person's approval. */
export const needsPersonMilestoneApproval = (milestone: Milestone): boolean =>
  !isMilestoneInDispute(milestone) &&
  milestone.companyAttestation === 'approved' &&
  canAttestMilestone(milestone, 'person');

const orderIndex = (kind: PersonTodoKind) => PERSON_TODO_ORDER.indexOf(kind);

/**
 * The active to-do rows for a homeowner, in canonical order. A row appears only
 * while its condition holds; within a kind, input order is kept.
 */
export function getActivePersonTodos(input: PersonTodoInput): PersonTodo[] {
  const projectById = new Map(input.projects.map(p => [p.id, p]));
  const contractById = new Map(input.contracts.map(c => [c.id, c]));
  const projectDescription = (projectId: string | undefined) =>
    projectId ? projectById.get(projectId)?.description : undefined;
  const projectCompanyName = (projectId: string | undefined) =>
    projectId ? projectById.get(projectId)?.companyName : undefined;

  const todos: PersonTodo[] = [];
  const add = (kind: PersonTodoKind, id: string, fields: Omit<PersonTodo, 'key' | 'kind' | 'priority'>) =>
    todos.push({ key: `${kind}:${id}`, kind, priority: PERSON_TODO_PRIORITY[kind], ...fields });
  const lastSigned = (contract: Contract | undefined) =>
    [contract?.companySignedAt, contract?.personSignedAt].filter(Boolean).sort().at(-1) ?? null;

  // Payments — signed contracts whose funding is not yet in place. A payment
  // that no longer applies is left out (ClickUp 869fj6nkx): the contract was
  // replaced (e.g. by an ÄTA) or the project is completed.
  const isStalePayment = (action: PersonFundingAction) => {
    const contract = contractById.get(action.contractId);
    if (projectById.get(action.projectId)?.status === 'completed') return true;
    return !!contract && (!!contract.supersededByContractId || (contract.versionStatus ?? 'current') !== 'current');
  };
  input.fundingActions
    .filter(action => action.canPay && action.remainingFundingNeedOre > 0 && !isStalePayment(action))
    .forEach(action =>
      add('payment', action.contractId, {
        since: lastSigned(contractById.get(action.contractId)),
        projectId: action.projectId,
        projectDescription: projectDescription(action.projectId),
        companyName: projectCompanyName(action.projectId),
        contractId: action.contractId,
        contractNumber: action.contractNumber,
        amountOre: action.remainingFundingNeedOre,
        paymentPending: action.status === 'pending',
      })
    );

  // Milestones — approval and disputes, only on contracts signed by both.
  input.milestones.forEach(milestone => {
    const contract = contractById.get(milestone.contractId);
    if (!contract || contract.draftStatus !== 'signed_by_both') return;
    const fields = {
      projectId: contract.projectId,
      projectDescription: projectDescription(contract.projectId),
      companyName: projectCompanyName(contract.projectId),
      contractId: contract.id,
      contractNumber: contract.contractNumber,
      since: isMilestoneInDispute(milestone) ? milestone.disputedAt ?? milestone.updatedAt : milestone.submittedAt ?? milestone.updatedAt,
      milestoneId: milestone.id,
      milestoneNumber: milestone.milestoneNumber,
      milestoneDescription: milestone.description,
    };
    // The same steps as the timeline (ClickUp 869fh4948, 869fmv7b5): an
    // objection until someone starts conflict mode, then a conflict.
    if (isMilestoneInDispute(milestone)) add(isMilestoneInConflict(milestone) ? 'conflict' : 'objection', milestone.id, fields);
    else if (needsPersonMilestoneApproval(milestone)) add('approveMilestone', milestone.id, fields);
  });

  // Messages are not tasks: they show next to Meddelanden in the menu
  // (ClickUp 869fj3q97).

  // Contracts — signing first; a draft still under review is a separate row.
  input.contracts.forEach(contract => {
    const fields = {
      projectId: contract.projectId,
      projectDescription: projectDescription(contract.projectId),
      companyName: projectCompanyName(contract.projectId),
      contractId: contract.id,
      contractNumber: contract.contractNumber,
      contractDescription: contractTitle(contract),
      revised: isRevisedDraft(contract),
      since: contract.signingSentAt ?? contract.draftSentAt ?? null,
    };
    if (canSignContract(contract, 'person')) add('signContract', contract.id, fields);
    else if (needsPersonDraftReview(contract)) add('reviewContract', contract.id, fields);
  });

  // Profile — only once it has loaded.
  if (input.profile && !isPersonProfileComplete(input.profile)) {
    add('completeProfile', 'me', {});
  }

  // Most important first, then what has waited longest (ClickUp 869fj6nkx);
  // the kind order breaks ties so the list stays stable.
  const byKind = todos
    .map((todo, index) => ({ todo, index }))
    .sort((a, b) => orderIndex(a.todo.kind) - orderIndex(b.todo.kind) || a.index - b.index)
    .map(({ todo }) => todo);
  return sortByPriority(byKind, todo => todo.priority, todo => todo.since);
}
